import { createTUID, GROUND_TERMINAL_UID, Orientation, orientationOfVector, } from "@buildwithflux/models";
import { brandedEntries, brandedKeys, compareVec2, globalFallbackLogger, guid, keySort, roundVec2FloatError, } from "@buildwithflux/shared";
import { applyChange, diff } from "deep-diff";
import { cloneDeep, groupBy, minBy, pickBy, uniq } from "lodash";
import { vec2 } from "../common";
import { ElementHelper, SymbolLayout } from "../elements";
import { isTerminalEqual } from "./helpers";
import { WireProjectionSimple } from "./WireProjectionSimple";
import { normalizeConnectedWires } from "./WiringNormalization";
// For brevity, since we use these values a lot:
const { HORIZONTAL, VERTICAL } = Orientation;
export class Wiring {
    elements;
    getDerivedElementDataOverride;
    getVertexForTerminalOverride;
    vertices = new Map();
    lastVerticesCommitted = {};
    constructor(vertices, elements, getDerivedElementDataOverride, getVertexForTerminalOverride) {
        this.elements = elements;
        this.getDerivedElementDataOverride = getDerivedElementDataOverride;
        this.getVertexForTerminalOverride = getVertexForTerminalOverride;
        if (vertices) {
            this.loadVertices(vertices);
        }
    }
    // NOTE: this is slow because of deep-diff
    // TODO given that it would be ultra-helpful to keep a set of changed vertices for rendering, we
    // should reuse that logic for getting the updates here too (though they'll need to be separate
    // sets because they need to be reset at different times; but we can probably share the mechanism)
    getVertexUpdates() {
        const frozen = this.serializeVertices();
        const changeSet = diff(this.lastVerticesCommitted, frozen);
        this.lastVerticesCommitted = frozen;
        return changeSet ?? [];
    }
    // NOTE: this is slow because of deep-diff
    // TODO: keep track of changes as they happen so that we do not need to diff
    getWiringUpdates() {
        return {
            routeUpdates: [], // Routes are deprecated. Don't generate them any more, but leave a stub to avoid changing too much in one PR.
            vertexUpdates: this.getVertexUpdates(),
        };
    }
    applyChangesToDocument(document) {
        const { vertexUpdates } = this.getWiringUpdates();
        for (const update of vertexUpdates) {
            applyChange(document.routeVertices, true, update);
        }
    }
    // This needs to be kept in sync with the get route and vertex updates methods as it is used
    // by RouteLoader to re-initalise for diffing when changes are made.
    updateLastCommited() {
        this.lastVerticesCommitted = this.serializeVertices();
    }
    // TODO: this function does not attempt to preserve existing vertices like RouteLoader does.
    // We should make them the same, but for now this is a quick fix to be able to instantiate
    // Wiring() on a one-off basis.
    loadVertices(rawVertices) {
        // TODO: sort order?
        const uids = new Set(Object.keys(rawVertices));
        const vertexList = Object.values(rawVertices)
            .sort((a, b) => a.id?.localeCompare(b.id))
            .filter((v) => !!v.position)
            .map((vertex) => ({
            position: { ...vertex.position },
            id: vertex.id,
            // Filter out connections to non-existent vertices, to repair possible data corruption
            connectedVertices: pickBy(vertex.connectedVertices, (_t, uid) => uids.has(uid)),
            terminal: vertex.terminal ? { ...vertex.terminal } : null,
        }));
        for (const vertex of vertexList) {
            this.vertices.set(vertex.id, vertex);
        }
        this.updateLastCommited();
    }
    disconnectElements(elementUids) {
        const elementUidsSet = new Set(elementUids);
        const affectedVertices = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        this.vertices.forEach((vertex) => {
            if (vertex.terminal && elementUidsSet.has(vertex.terminal.element_uid)) {
                vertex.terminal = null;
                affectedVertices.push(vertex.id);
            }
        });
        return affectedVertices;
    }
    removeWireSegments(segmentsToRemove) {
        const affectedVertices = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        segmentsToRemove.forEach(([from, to]) => {
            this.disconnectVertices(from, to);
            affectedVertices.push(from, to);
        });
        // Remove any vertices that no longer have any connections:
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        this.filterVertices((v) => !Object.keys(v.connectedVertices).length).forEach((vertex) => {
            this.vertices.delete(vertex.id);
        });
        affectedVertices.push(...this.normalizeConnectedNets(affectedVertices.filter((uid) => this.vertices.has(uid))));
        return uniq(affectedVertices);
    }
    /** Safely disconnect and remove specified vertices */
    removeVertices(vertices) {
        for (const vertexId of vertices) {
            const vertex = this.vertices.get(vertexId);
            if (!vertex) {
                // Already removed.
                continue;
            }
            // Important: we iterate a copy of .connectedVertices keys below,
            // because `disconnectVertices` mutates the source array during iteration.
            for (const connectedId of brandedKeys(vertex.connectedVertices)) {
                this.disconnectVertices(vertex.id, connectedId);
            }
            this.vertices.delete(vertexId);
        }
    }
    /** Ensure that a given wire target exists in the project (eg that any specified terminal and
     * vertices exist). A site can become invalid eg if wires are modified (perhaps by another user)
     * while the user is in wiring mode.
     */
    wireConnectionTargetIsValid(site) {
        // Returns true if the given WireConnectionTarget represents a valid connection point.
        if (site.terminal) {
            // Terminal: check that the terminal exists on an existing element.
            const terminal = site.terminal;
            const elementData = this.getDerivedElementData(terminal.element_uid);
            if (!elementData)
                return false;
            if (!elementData.absolutePinLocations[terminal.terminal_uid])
                return false;
            return true;
        }
        else if (site.vertex) {
            // Vertex: check that the vertex exists
            return this.vertices.has(site.vertex);
        }
        else if (site.segment) {
            // Segment: check that it is a segment between two existing vertices
            const [from, to] = site.segment;
            return this.vertices.has(from) && this.vertices.has(to);
        }
        else if (site.position) {
            // Position: minimally require that it has x and y
            const pos = site.position;
            return pos && typeof pos.x === "number" && typeof pos.y === "number";
        }
        // If none of the above, it's not a valid connection target
        return false;
    }
    /** Given a wire connection target, return the complete site info for that target.
     *
     * If inconsistent data is passed in, ensure the returned value is consistent -- eg for
     * terminals and vertices, always provide the terminal/vertex position, not the passed-in
     * position; for segments, ensure the returned position is exactly on the segment, to prevent
     * contradictory data being generated by fuzziness in the caller.
     */
    getWireConnectionSite(at) {
        if (!this.wireConnectionTargetIsValid(at)) {
            throw new Error(`Wire connection target does not exist in project: ${JSON.stringify(at)}`);
        }
        let position, vertex, terminal, segment;
        // `if (at.terminal)` causes a typescript error because `terminal` may not be a field on `at`.
        // But `if ('terminal' in at)` allows the caller to pass in `{vertex: 'validuid', terminal: undefined}`
        // without typescript error, but would produce runtime error.
        if (at.terminal) {
            terminal = at.terminal;
            const derivedElementData = this.getDerivedElementData(terminal.element_uid);
            const terminalPosition = derivedElementData.absolutePinLocations[terminal.terminal_uid]?.position;
            position = { ...terminalPosition };
            const vertexUid = this.getVertexForTerminal(terminal);
            vertex = vertexUid ? this.vertices.get(vertexUid) : undefined;
        }
        else if (at.vertex) {
            vertex = this.vertices.get(at.vertex);
            position = { ...vertex.position };
            terminal = vertex.terminal;
        }
        else if (at.segment) {
            segment = at.segment;
            const vertices = this.getVertices(segment);
            position = { ...(at.position ?? vertices[0].position) };
            // Ensure position is actually on segment, for data consistency.
            if (orientationOfVector(vec2.sub(vertices[0].position, vertices[1].position)) === HORIZONTAL) {
                position = { x: position.x, y: vertices[0].position.y };
            }
            else {
                position = { x: vertices[0].position.x, y: position.y };
            }
        }
        else if (at.position) {
            // Note: don't try and find a terminal/vertex/segment at the given position; this function
            // is meant to fill in actually linked data (like vertices and their terminals), not
            // happen-to-overlap-in-space data.
            position = { ...at.position };
        }
        else {
            throw new Error("Must specify `position` when calling getWireConnectionSite with no other data");
        }
        // Always round off float error no matter what, or we'll get subtle misalignments later, as
        // these positions are used for comparisons and creating final vertex positions from.
        position = roundVec2FloatError(position);
        if (vertex && terminal) {
            return { position, vertex: vertex.id, terminal };
        }
        else if (vertex) {
            return { position, vertex: vertex.id };
        }
        else if (terminal) {
            return { position, terminal };
        }
        else if (segment) {
            return { position, segment };
        }
        else {
            return { position };
        }
    }
    /** The main wire creation operation. Places a wire from the last-clicked routing
     * point to the just-clicked one, keeping everything well-formed.
     */
    placeWire(from, to, projectionAlgo = new WireProjectionSimple(), opts = {}) {
        let affectedVertices = [];
        const fromSite = this.getWireConnectionSite(from);
        const toSite = this.getWireConnectionSite(to);
        const fromVertex = this.ensureVertexAtConnectionSite(fromSite);
        affectedVertices.push(fromVertex);
        // Connect projected vertice(s).
        const projectedRoute = projectionAlgo.project(fromSite, toSite);
        let prev = fromVertex;
        for (const { direction, position } of projectedRoute.slice(1, -1)) {
            const vertex = this.addVertex(position);
            this.connectVertices(prev, vertex.id, direction);
            affectedVertices.push(vertex.id);
            prev = vertex.id;
        }
        let toVertex = this.ensureVertexAtConnectionSite(toSite);
        // Connect the end vertex to the previous one.
        this.connectVertices(prev, toVertex, projectedRoute.at(-1)?.direction);
        affectedVertices.push(toVertex);
        if (opts.normalize ?? true) {
            affectedVertices = this.normalizeConnectedWires(toVertex);
        }
        // Normalization can change which vertices exist. Get the nearest one if it changes:
        toVertex = minBy(affectedVertices, (uid) => {
            const v = this.vertices.get(uid);
            return v ? vec2.distance(v.position, toSite.position) : Infinity;
        });
        return [affectedVertices, toVertex];
    }
    getAllNetVerticesAcrossPortals(vertexId) {
        function makePortalGroupId(element) {
            // Note: this uses equivalent logic to the analogous code in netMapping.ts
            return ElementHelper.isPortal(element)
                ? element.label ?? ""
                : ElementHelper.isGroundElement(element)
                    ? GROUND_TERMINAL_UID
                    : undefined;
        }
        const netVertices = this.getAllConnectedVertices(vertexId);
        const portals = Object.values(netVertices)
            .map((uid) => this.elements[this.vertices.get(uid)?.terminal?.element_uid])
            .filter((elem) => !!elem && (ElementHelper.isPortal(elem) || ElementHelper.isGroundElement(elem)));
        // TODO this needs to recurse! Currently it'll only detect other subnets directly connected to this subnet's
        // portals (which is most cases!), but it won't detect cases where those subnets are connected to
        // different subnets through differently-named portals.
        if (portals.length) {
            const verticesByElementUid = groupBy([...this.vertices.values()], (v) => v.terminal?.element_uid);
            const allPortalsByGroup = groupBy(Object.values(this.elements), (e) => makePortalGroupId(e));
            for (const portal of portals) {
                const portalGroupId = makePortalGroupId(portal);
                if (!(portalGroupId && portalGroupId in allPortalsByGroup))
                    continue;
                for (const other of allPortalsByGroup[portalGroupId]) {
                    if (other.uid === portal.uid)
                        continue;
                    for (const otherVertex of verticesByElementUid[other.uid] ?? []) {
                        netVertices.push(...this.getAllConnectedVertices(otherVertex.id));
                    }
                }
            }
        }
        return uniq(netVertices);
    }
    /** Get all vertex connected (directly or indirectly) to the given vertex, but only
     * traversing until we run into a vertex that doesn't match the matching() condition.
     * If `includeUnmatchingLeafVertices` is true, also return any unmatching vertices
     * directly connected to the matching vertices.
     *
     * Also include the given vertex in the returned list according to the same logic.
     *
     * Does not cross portals (use `getAllNetVerticesAcrossPortals` for that).
     */
    getAllConnectedVertices(vertexId, matching, includeUnmatchingLeafVertices = false, visited = {}) {
        const vertex = this.vertices.get(vertexId);
        if (!vertex) {
            throw new Error(`Attempt to retrieve non-existent connected vertex ${vertexId}`);
        }
        if (matching && !matching(vertex)) {
            return includeUnmatchingLeafVertices ? [vertexId] : [];
        }
        visited[vertexId] = vertex;
        const found = [vertexId];
        for (const connectedId of brandedKeys(vertex.connectedVertices)) {
            if (connectedId in visited)
                continue;
            found.push(...this.getAllConnectedVertices(connectedId, matching, includeUnmatchingLeafVertices, visited));
        }
        return found;
    }
    /** Walk the graph of vertices that are connected to the given vertex, stopping on the first
     * one that matches `until` and returning its uid.
     */
    walkConnectedVertices(vertexId, until, visited = {}) {
        const vertex = this.vertices.get(vertexId);
        if (!vertex) {
            throw new Error(`Attempt to retrieve non-existent connected vertex ${vertexId}`);
        }
        if (until && until(vertex)) {
            return vertexId;
        }
        visited[vertexId] = vertex;
        for (const connectedId of brandedKeys(vertex.connectedVertices)) {
            if (connectedId in visited)
                continue;
            const found = this.walkConnectedVertices(connectedId, until, visited);
            if (found) {
                return found;
            }
        }
        return undefined;
    }
    /** Return a list of wires (each wire is a chain of singly-connected wire segments up until a
     * branchpoint or terminal) connected to the given vertex. If the vertex has 2 outgoing
     * connections, it is considered a single wire (i.e. not two wires separated by the vertex),
     * unless it's a terminal-connected vertex. If the vertex is 3+-way connected, then each
     * outgoing connection is a separate wire.
     *
     * For consistency, each wire includes the given vertex too (in the middle if it's a mid-wire
     * vertex)
     *
     * Note: for now, this only returns a valid result if the input vertex is a wire endpoint (i.e.
     * a terminal, loose endpoint, or branchpoint). The given vertex is always the first
     * entry.
     */
    getDirectlyConnectedWires(vertexId) {
        const isBranchVertex = (vertexId) => {
            const vertex = this.vertices.get(vertexId);
            return vertex?.terminal || Object.keys(vertex?.connectedVertices ?? {}).length > 2;
        };
        let wires = [];
        const vertex = this.vertices.get(vertexId);
        for (const connectedId of brandedKeys(vertex?.connectedVertices ?? {})) {
            let wire = this.getAllConnectedVertices(connectedId, (v) => !isBranchVertex(v.id), true);
            // TODO very bad, will fail if starting from a non-branchpoint vertex:
            wire = wire.filter((v) => v !== vertexId);
            if (!wire[0]) {
                // Should never get here, since at least the connected vertex must always be
                // included. Fail loudly if we do.
                throw new Error("getAllConnectedVertices returned empty list even though includeUnmatchingLeafVertices=true");
            }
            wires.push([vertexId, ...wire]);
        }
        if (!isBranchVertex(vertexId) && wires.length === 2) {
            const leading = wires[0].slice(1).reverse();
            const trailing = wires[1];
            wires = [[...leading, ...trailing]];
        }
        return wires;
    }
    getNetIDForVertex(doc, vertexId) {
        const netsByPin = Object.fromEntries(Object.values(doc.nets).flatMap((n) => Object.keys(n.pins).map((p) => [p, n.uid])));
        const vertexWithTerminal = this.walkConnectedVertices(vertexId, (v) => !!v.terminal);
        const terminal = vertexWithTerminal ? this.vertices.get(vertexWithTerminal)?.terminal : undefined;
        return terminal ? netsByPin[createTUID(terminal)] : undefined;
    }
    filterVertices(predicate) {
        // `unknown`, not `boolean` to allow for truthy/falsy while still being typesafe (prevent using result other than as a condition)
        const vertices = [];
        for (const vertex of this.vertices.values()) {
            if (predicate(vertex))
                vertices.push(vertex);
        }
        return vertices;
    }
    findVertex(predicate) {
        // `unknown`, not `boolean` to allow for truthy/falsy while still being typesafe
        for (const vertex of this.vertices.values()) {
            if (predicate(vertex))
                return vertex;
        }
        return undefined;
    }
    getVertices(uids) {
        return uids.map((uid) => this.vertices.get(uid)).filter((v) => !!v);
    }
    addVertex(position) {
        const vertex = { position: { ...position }, id: guid(), connectedVertices: {}, terminal: null };
        this.vertices.set(vertex.id, vertex);
        return vertex;
    }
    /**
     * Given a set of vertices, normalize all nets connected to any of those vertices.
     */
    normalizeConnectedNets(vertices) {
        const seenVertices = new Set();
        const uniqueNetVertices = new Set();
        for (const vertexId of vertices) {
            if (seenVertices.has(vertexId))
                continue;
            uniqueNetVertices.add(vertexId);
            const connectedVertices = this.getAllConnectedVertices(vertexId);
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            connectedVertices.forEach((uid) => seenVertices.add(uid));
        }
        const affectedVertices = [];
        for (const vertex of uniqueNetVertices) {
            affectedVertices.push(...this.normalizeConnectedWires(vertex));
        }
        return uniq(affectedVertices);
    }
    /** Given a vertex, normalize all the wires in the same net (but not across portals). */
    normalizeConnectedWires(vertexId) {
        return normalizeConnectedWires(this, vertexId, globalFallbackLogger);
    }
    /** Low-level vertex disconnect. Leaves the datastructure consistent (i.e. removes both
     * sides of the double-link) but doesn't attempt to clean up high-level wire structure
     * like adjacent wire segments etc.
     */
    disconnectVertices(from, to) {
        const fromVertex = this.vertices.get(from);
        const toVertex = this.vertices.get(to);
        delete fromVertex?.connectedVertices[to];
        delete toVertex?.connectedVertices[from];
    }
    /** Low-level vertex connection. Leaves the datastructure in consistent state but doesn't do
     * high-level things like adding corners, merging vertices, etc. Leaves vertex count unchanged,
     * and fails hard on illegal operations.
     * Explicitly specify orientation=undefined for orientation to be selected automatically.
     */
    connectVertices(fromId, toId, orientation) {
        const from = this.vertices.get(fromId);
        const to = this.vertices.get(toId);
        if (!from || !to)
            throw new Error(`Attempt to connect non-existent vertex ${fromId} or ${toId}`);
        const direction = vec2.sub(from.position, to.position);
        orientation =
            orientation ??
                // JS div-by-0 gives Infinity so the below works.
                (Math.abs(direction.x / direction.y) > 1 ? HORIZONTAL : VERTICAL);
        if (!(to.id in from.connectedVertices)) {
            from.connectedVertices[to.id] = orientation;
        }
        if (!(from.id in to.connectedVertices)) {
            to.connectedVertices[from.id] = orientation;
        }
    }
    splitSegment(fromId, toId, midPoints) {
        const from = this.vertices.get(fromId);
        const to = this.vertices.get(toId);
        if (!from || !to)
            throw new Error(`Vertex ${fromId} or ${toId} does not exist`);
        const connectionType = brandedEntries(from.connectedVertices).find(([uid, _type]) => uid === to.id)?.[1];
        if (!connectionType) {
            throw new Error("Cannot split nonexistent connection");
        }
        const midVertices = Array(midPoints.length).fill(undefined);
        let prev = from;
        this.disconnectVertices(from.id, to.id);
        // Sorting by "scalar projection" would be more correct in that it would ensure
        // ascending order of points outside the endpoints. But we're not trying to
        // handle those cases, so distance is fine.
        const sortedMidpoints = midPoints
            .map((p, idx) => ({ point: p, idx }))
            .sort(keySort((p) => vec2.distance(from.position, p.point)));
        for (const { point, idx } of sortedMidpoints) {
            const vertex = compareVec2(prev.position, point)
                ? prev
                : compareVec2(to.position, point)
                    ? to
                    : this.addVertex({ ...point });
            if (vertex !== prev) {
                this.connectVertices(prev.id, vertex.id, connectionType);
            }
            midVertices[idx] = vertex;
            prev = vertex;
        }
        if (prev !== to) {
            this.connectVertices(prev.id, to.id, connectionType);
        }
        return midVertices.map((v) => v.id);
    }
    serializeVertices() {
        return cloneDeep(Object.fromEntries(this.vertices.entries()));
    }
    /** TODO this shouldn't really be a public method; it's not part of the semantics of wiring really. But it's
     * used by WireProjectionSmart for efficiency, and WireProjectionSmart is very wiring related, so I'm ok
     * "keeping it in the family" for now.
     */
    getDerivedElementData(elementUid) {
        if (this.getDerivedElementDataOverride) {
            return this.getDerivedElementDataOverride(elementUid);
        }
        // TODO we'll probably wish to store/cache the derived element data in a map in this class, so multiple
        // operations in sequence can be fast. For now, I'm ignoring that, since I'm just refactoring the existing
        // behaviour.
        const elementData = this.elements[elementUid];
        if (!elementData)
            return undefined;
        const symbolInfo = SymbolLayout.getSymbolInfo(elementData);
        const symbolPositions = ElementHelper.getSymbolPlacements(elementData, symbolInfo);
        const absolutePinLocations = ElementHelper.getAbsoluteTerminalPlacements(elementData, symbolInfo);
        return {
            absolutePinLocations,
            symbolInfo,
            symbolPositions,
        };
    }
    /** Make a connection point at position constrained to the given subject, or canvas if
     * not specified, keeping the datastructure well-formed.
     */
    ensureVertexAtConnectionSite(at) {
        let vertexId;
        if (at.vertex) {
            vertexId = at.vertex;
        }
        else if (at.terminal) {
            // TODO we shouldn't need to look up vertex here anymore, because we require
            // `WireConnectionSite` to specify the vertex if it exists, so it'll match the previous
            // `at.vertex` condition. But we don't have complete guarantees of that yet (eg from
            // SubjectDragControls producing incomplete WireConnectionSite objects), so keep this
            // here.
            vertexId = this.findVertex((v) => v.terminal && isTerminalEqual(v.terminal, at.terminal))?.id;
            if (!vertexId) {
                const vertex = this.addVertex(at.position);
                vertex.terminal = at.terminal;
                vertexId = vertex.id;
            }
        }
        else if (at.segment) {
            [vertexId] = this.splitSegment(...at.segment, [at.position]);
        }
        else {
            vertexId = this.addVertex(at.position).id;
        }
        return vertexId;
    }
    getVertexForTerminal(terminal) {
        if (this.getVertexForTerminalOverride) {
            return this.getVertexForTerminalOverride(terminal);
        }
        // TODO we'll definitely want to create a per-instance inverse terminal-to-vertex mapping to make this fast
        // for multiple operations. For now though I'm just refactoring existing behaviour.
        // But note: we'll need to make sure when we implement it that this mapping is updated both in loadVertices
        // and in VertexLoader.ts (if it still exists by then), not just on instantiation.
        return this.findVertex((v) => v.terminal && isTerminalEqual(v.terminal, terminal))?.id;
    }
}
//# sourceMappingURL=Wiring.js.map